import React, { useMemo, useState } from 'react';
import { createProblem } from '../firebase/problemService';
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Link } from 'react-router-dom';
import { useAuth } from '../config/AuthContext';
import SEO, { seoConfig } from '../component/SEO';
import { toastedError, toastedSuccess } from '../config/toast';
import PageShapes from '../component/PageShapes';

const SubmitProblem = () => {

     const { user, userData } = useAuth();
     const defaultName = userData?.name || 'Unknown User';

     const defaultInitials = useMemo(() => {
          const nameParts = defaultName.trim().split(' ').filter(Boolean);
          return nameParts.slice(0, 2).map((part) => part[0]).join('').toUpperCase();
     }, [defaultName]);

     // 🔥 SINGLE CONTENT STATE
     const [content, setContent] = useState('');
     const [problemText, setProblemText] = useState('');
     const [titleIdea, setTitleIdea] = useState('');


     const [formData, setFormData] = useState({
          type: "Idea",
          category: '',
          visibility: 'Public',
     });

     const [loading, setLoading] = useState(false);
     const [successText, setSuccessText] = useState('');
     const [errorText, setErrorText] = useState('');
     const [showInstagram, setShowInstagram] = useState(true);

     const handleChange = (event) => {
          const { name, value } = event.target;

          setFormData((prev) => ({
               ...prev,
               [name]: value,
          }));
     };

     const handleVisibilityChange = (value) => {
          setFormData((prev) => ({
               ...prev,
               visibility: value,
          }));
     };

     const handleTypeChange = (value) => {
          setFormData((prev) => ({
               ...prev,
               type: value
          }));
     };


     // 🔥 SUBMIT
     const handleSubmit = async (event) => {
          event.preventDefault();

          setSuccessText('');
          setErrorText('');

          if (!user) {
               setErrorText('Please login first.');
               toastedError("Please login to continue");
               return;
          }

          if (titleIdea.trim().length < 15) {
               toastedError("Please title in a bit more detail");
               return;
          }

          if (!content || content.trim() === '<p><br></p>') {
               setErrorText('Content is required.');
               toastedError("Write something to publish");
               return;
          }

          if (!titleIdea || titleIdea.trim() === '<p><br></p>') {
               setErrorText('title is required.');
               toastedError("Write something to publish");
               return;
          }

          if (!problemText.trim() && (!content || content === '<p><br></p>')) {
               setErrorText('Please write something.');
               return;
          }

          try {
               setLoading(true);

               await createProblem({
                    uid: user?.uid,
                    userName: userData?.name || user?.displayName || 'Unknown User',
                    userInitials: defaultInitials,
                    category: formData.category,
                    visibility: formData.visibility,
                    type: formData.type,
                    instagram: showInstagram ? (userData?.instagram || null) : null,
                    content: content,
                    problem: problemText,
                    title: titleIdea,
               });

               setSuccessText('Problem/Idea published successfully');
               toastedSuccess("Idea published successfully");


               setContent('');
               setProblemText('');
               setTitleIdea('')

               setFormData({
                    type: "Idea",
                    category: '',
                    visibility: 'Public',
               });



          } catch (error) {
               console.error('Create problem error:', error);
               toastedError("Failed to publish. Try again");
          } finally {
               setLoading(false);
               setTimeout(() => {
                    setSuccessText('')
               }, 4000);
          }
     };

     return (
          <>
               <PageShapes />
               <SEO {...seoConfig.submit} />

               {loading && (
                    <div className="fullscreen-loader">
                         <div className="loader-spinner" />
                         <span className="loader-text">Publishing your {formData.type === "Problem" ? 'Problem' : 'Idea'}...</span>
                    </div>
               )}

               <div className="forge-problem-view">

                    <main className="forge-container">
                         <header className="forge-header">
                              <div className="badge">New Concept</div>
                              <h1>Write Your <span>{formData.type === "Problem" ? 'Problem' : 'Idea'} Like a Story</span></h1>
                              <p>All your writing, editing, and publishing - unified in one powerful place.</p>
                         </header>

                         <form className="forge-form-glass" onSubmit={handleSubmit}>

                              <div className='row row-gap-4'>

                                   <div className='col-md-6'>
                                        <div className="input-group">
                                             <label>Category</label>
                                             <select
                                                  name="category"
                                                  value={formData.category}
                                                  onChange={handleChange}
                                                  required
                                             >
                                                  <option value="">-</option>
                                                  <option value="Tech">Tech</option>
                                                  <option value="AI & ML">AI & ML</option>
                                                  <option value="SaaS">SaaS</option>
                                                  <option value="Fintech">Fintech</option>
                                                  <option value="EdTech">EdTech</option>
                                                  <option value="HealthTech">HealthTech</option>
                                                  <option value="E-commerce">E-commerce</option>
                                                  <option value="Social Media">Social Media</option>
                                                  <option value="Creator Economy">Creator Economy</option>
                                                  <option value="Design">Design</option>
                                                  <option value="Productivity">Productivity</option>
                                                  <option value="Marketing">Marketing</option>
                                                  <option value="Automation">Automation</option>
                                                  <option value="Cybersecurity">Cybersecurity</option>
                                                  <option value="Blockchain & Web3">Blockchain & Web3</option>
                                                  <option value="Gaming">Gaming</option>
                                                  <option value="Entertainment">Entertainment</option>
                                                  <option value="Travel">Travel</option>
                                                  <option value="Food & Beverage">Food & Beverage</option>
                                                  <option value="Automotive">Automotive</option>
                                                  <option value="Real Estate">Real Estate</option>
                                                  <option value="Logistics">Logistics</option>
                                                  <option value="Agriculture">Agriculture</option>
                                                  <option value="Environment">Environment</option>
                                                  <option value="Community">Community</option>
                                                  <option value="Non-Profit">Non-Profit</option>
                                                  <option value="Education">Education</option>
                                                  <option value="Lifestyle">Lifestyle</option>
                                                  <option value="Fashion">Fashion</option>
                                                  <option value="Sports">Sports</option>
                                                  <option value="Other">Other</option>
                                             </select>
                                        </div>
                                   </div>

                                   <div className='col-md-6'>
                                        <div className="input-group">
                                             <label>Type</label>

                                             <div className="radio-toggle type-toggle">
                                                  <button
                                                       type="button"
                                                       className={formData.type === 'Idea' ? 'idea-active' : ''}
                                                       onClick={() => handleTypeChange('Idea')}
                                                  >
                                                       Idea
                                                  </button>

                                                  <button
                                                       type="button"
                                                       className={formData.type === 'Problem' ? 'problem-active' : ''}
                                                       onClick={() => handleTypeChange('Problem')}
                                                  >
                                                       Problem
                                                  </button>
                                             </div>
                                        </div>

                                        <p className={`message-note ${formData.type?.toLocaleLowerCase()}`}>
                                             {formData.type === "Problem" ? (
                                                  <>💡 You’re sharing a problem. Others can explore and suggest solutions.</>
                                             ) : (
                                                  <>💡 You’re sharing an idea. Others can discover and engage with it.</>
                                             )}
                                        </p>
                                   </div>

                                   <div className='col-12'>
                                        <div className="input-group">
                                             <label> {formData.type === "Problem" ? 'Problem' : 'Idea'} title</label>

                                             <input
                                                  type='text'
                                                  className="form-control"
                                                  value={titleIdea}
                                                  onChange={(e) => {
                                                       const value = e.target.value;
                                                       setTitleIdea(value.slice(0, 150));
                                                  }}
                                                  placeholder={`Shortly explain your ${formData.type === "Problem" ? 'Problem' : 'Idea'}`}
                                                  required
                                             />

                                             <small className="char-count">
                                                  {titleIdea.length}/150
                                             </small>

                                        </div>
                                   </div>

                                   <div className='col-12'>
                                        <div className="input-group">
                                             <label>{formData.type === "Problem" ? 'Problem' : 'Idea'} (in simple words)</label>

                                             <textarea
                                                  className="form-control"
                                                  value={problemText}
                                                  onChange={(e) => {
                                                       const value = e.target.value;
                                                       setProblemText(value.slice(0, 800));
                                                  }}
                                                  placeholder={`Briefly describe your ${formData?.type === "Problem" ? 'Problem' : 'Idea'} in a clear and simple way...`}
                                                  rows="6"
                                                  required
                                                  maxLength={800}
                                             />

                                             <small className="char-count">
                                                  {problemText?.length}/800
                                             </small>

                                        </div>
                                   </div>


                                   <div className='col-12'>
                                        <div className="input-group">
                                             <label>Write Your Idea</label>

                                             <ReactQuill
                                                  value={content}
                                                  onChange={setContent}
                                                  placeholder={`Got an ${formData.type === "Problem" ? 'Problem' : 'Idea'}? Write it here in one clear, impactful sentence...`}
                                                  required
                                             />
                                        </div>
                                   </div>


                                   <div className='col-md-6'>
                                        <div className="input-group">
                                             <label>Visibility</label>
                                             <div className="radio-toggle">
                                                  <button
                                                       type="button"
                                                       className={formData.visibility === 'Public' ? 'active' : ''}
                                                       onClick={() => handleVisibilityChange('Public')}
                                                  >
                                                       Public
                                                  </button>
                                                  <button
                                                       type="button"
                                                       className={formData.visibility === 'Private' ? 'active' : ''}
                                                       onClick={() => handleVisibilityChange('Private')}
                                                  >
                                                       Private
                                                  </button>
                                             </div>
                                        </div>
                                        {!user ?
                                             <p className="message-note danger">
                                                  💡 Please login first.
                                             </p>
                                             : <>
                                                  {formData.visibility === 'Private' && (
                                                       <p className="message-note success">
                                                            💡 This {formData.type === "Problem" ? 'Problem' : 'Idea'} will remain private and only visible to you.
                                                       </p>
                                                  )}

                                                  {formData.visibility === 'Public' && (
                                                       <p className="message-note success">
                                                            💡 This {formData.type === "Problem" ? 'Problem' : 'Idea'} will be visible to everyone.
                                                       </p>
                                                  )}
                                             </>
                                        }


                                   </div>

                                   <div className='col-md-6'>
                                        <div className="input-group">
                                             <label>Your Identity</label>
                                             <div className="identity-badge">
                                                  <div className="avatar">{defaultInitials}</div>
                                                  <input type="text" value={defaultName} readOnly />
                                             </div>
                                        </div>

                                        {defaultName === 'Unknown User' ? (
                                             <p className="message-note danger">
                                                  <span className="icon">💡</span>
                                                  Posted anonymously — your identity will not be shown.
                                             </p>
                                        ) : (
                                             <p className="message-note success">
                                                  <span className="icon">✔</span>
                                                  Your idea will be published with your name.
                                             </p>
                                        )}
                                   </div>


                                   {userData?.instagram && formData?.visibility === 'Public' && (
                                        <div className='col-12'>
                                             <div className="insta-toggle">
                                                  <label>
                                                       <input
                                                            type="checkbox"
                                                            checked={formData.visibility === 'Public' ? showInstagram : false}
                                                            onChange={(e) => setShowInstagram(e.target.checked)}
                                                       />
                                                       <div className=''>
                                                            <Link target='_blank' to={`https://www.instagram.com/${userData?.instagram}`}> @{userData?.instagram}</Link>
                                                            <p> {showInstagram ? 'show' : 'Hide'} Instagram with this {formData?.type === "Problem" ? 'Problem' : 'Idea'} </p>
                                                       </div>
                                                  </label>
                                             </div>
                                        </div>
                                   )}

                                   <div className='col-12'>
                                        {errorText && (
                                             <div className="form-message error-message text-danger">{errorText}</div>
                                        )}

                                        {successText && (
                                             <div className="form-message success-message text-success">{successText}</div>
                                        )}

                                        <div className="forge-actions heading-font">
                                             <Link to="/explore" className="btn-cancel" > Explore Now </Link>

                                             <button
                                                  type="submit"
                                                  className="btn-submit"
                                                  disabled={loading}
                                             >
                                                  {loading ? 'Publishing...' : 'Publish Idea'}
                                             </button>
                                        </div>
                                   </div>
                              </div>
                         </form>
                    </main>
               </div>

               <div className="forge-footer-content">
                    <div className='forge-container'>

                         <h2>Every Idea Starts Somewhere</h2>

                         <p>
                              Not every idea needs to be perfect. Some start as rough thoughts,
                              some as small problems — and that’s completely fine.
                         </p>

                         <p>
                              What matters is putting it out there. Writing it down helps you
                              understand it better, and sometimes, the right people notice it too.
                         </p>

                         <p>
                              Whether you're sharing a problem or an idea, this is your space
                              to think, express, and start something meaningful.
                         </p>

                         <p className="sub-text">
                              Just start. You can always refine it later.
                         </p>
                    </div>

               </div>
          </>

     );
};

export default SubmitProblem;